import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Chip, Divider } from '@mui/material';
import NotificationsTable from './NotificationsTable';
import SitesService from '../../../services/SitesService';
import SitesOrderService, { SITES_ORDER_DISPLAY_OFFSET } from '../../../services/SitesOrderService';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { useTranslation } from 'react-i18next';
import OrderDetailModal from '../orders/OrderDetailModal';
import SnackbarUtils from '../../../utils/SnackbarUtils';

const Notifications = () => {
  const { t } = useTranslation();

  const [notifications, setNotifications] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState(null);
  const [isReadFilter, setIsReadFilter] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  const searchTimeoutRef = useRef(null);
  
  // Estados para el modal de detalles de orden
  const [orderDetail, setOrderDetail] = useState(null);
  const [isOrderDetailModalOpen, setIsOrderDetailModalOpen] = useState(false);
  const [loadingOrderDetail, setLoadingOrderDetail] = useState(false);

  const fetchNotifications = useCallback(async (page, pageSize, search, type, isRead) => {
    try {
      setLoading(true);
      setError(null);
      const cleanSearch = search || '';
      const response = await SitesService.getNotifications(
        type || null,
        isRead !== null ? isRead : null,
        page,
        pageSize
      );
      
      if (response && typeof response === 'object') {
        setNotifications(response.notifications || response.data || []);
        setTotalRows(response.total || response.totalCount || 0);
      } else {
        setNotifications(response || []);
        setTotalRows(response?.length || 0);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err.response?.data?.message || 'Error al cargar notificaciones');
      setNotifications([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce para el campo de búsqueda
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  // Cargar datos cuando cambian los filtros o la paginación
  useEffect(() => {
    fetchNotifications(
      paginationModel.page + 1,
      paginationModel.pageSize,
      debouncedSearchTerm,
      typeFilter,
      isReadFilter
    );
  }, [fetchNotifications, paginationModel.page, paginationModel.pageSize, debouncedSearchTerm, typeFilter, isReadFilter]);

  // Funciones para manejar cambios de paginación
  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel);
  };

  // Función para manejar cambios de búsqueda
  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleTypeFilterChange = (newType) => {
    setTypeFilter(newType);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const handleIsReadFilterChange = (newIsRead) => {
    setIsReadFilter(newIsRead);
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'contact':
        return 'primary';
      case 'career':
        return 'success';
      case 'newsletter':
        return 'info';
      case 'order':
        return 'warning';
      case 'billing':
        return 'error';
      case 'b2b':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'contact':
        return t('contact') || 'Contacto';
      case 'career':
        return t('career') || 'Carrera';
      case 'newsletter':
        return t('newsletter') || 'Newsletter';
      case 'order':
        return t('order') || 'Orden';
      case 'billing':
        return t('billing') || 'Facturación';
      case 'b2b':
        return 'B2B';
      default:
        return type || '-';
    }
  };

  const extractOrderIdFromDescription = (description) => {
    // La descripción tiene formato: "Orden #10001 de ..." (número mostrado = offset + id interno)
    const match = description.match(/Orden #(\d+)/);
    if (!match) return null;
    const parsed = parseInt(match[1], 10);
    return parsed >= SITES_ORDER_DISPLAY_OFFSET ? parsed - SITES_ORDER_DISPLAY_OFFSET : parsed;
  };

  const handleView = async (notification) => {
    // Si es una notificación de orden, abrir el modal de detalles de orden
    if (notification.type === 'order') {
      const orderId = extractOrderIdFromDescription(notification.description);
      if (orderId) {
        try {
          setLoadingOrderDetail(true);
          const order = await SitesOrderService.getOrderById(orderId);
          setOrderDetail(order);
          setIsOrderDetailModalOpen(true);
          
          // Si la notificación no está leída, marcarla como leída
          if (!notification.isRead) {
            try {
              await SitesService.markNotificationAsRead(notification.id);
              // Refrescar la lista
              fetchNotifications(
                paginationModel.page + 1,
                paginationModel.pageSize,
                debouncedSearchTerm,
                typeFilter,
                isReadFilter
              );
            } catch (err) {
              console.error('Error marking notification as read:', err);
              // No mostrar error al usuario, solo log
            }
          }
        } catch (err) {
          console.error('Error loading order details:', err);
          SnackbarUtils.error(t('error_loading_order_details') || 'Error al cargar los detalles del pedido');
        } finally {
          setLoadingOrderDetail(false);
        }
        return;
      }
    }
    
    // Para otros tipos de notificaciones, mostrar el modal normal
    setSelectedNotification(notification);
    setIsViewModalOpen(true);
    
    // Si la notificación no está leída, marcarla como leída
    if (!notification.isRead) {
      try {
        await SitesService.markNotificationAsRead(notification.id);
        // Refrescar la lista
        fetchNotifications(
          paginationModel.page + 1,
          paginationModel.pageSize,
          debouncedSearchTerm,
          typeFilter,
          isReadFilter
        );
      } catch (err) {
        console.error('Error marking notification as read:', err);
        // No mostrar error al usuario, solo log
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await SitesService.markAllNotificationsAsRead();
      window.dispatchEvent(new CustomEvent('notifications-all-read'));
      // Refrescar la lista
      fetchNotifications(
        paginationModel.page + 1,
        paginationModel.pageSize,
        debouncedSearchTerm,
        typeFilter,
        isReadFilter
      );
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
      setError(err.response?.data?.message || 'Error al marcar todas las notificaciones como leídas');
    }
  };

  return (
    <Box p={3}>
      <PageHeader 
        title={t('notifications') || 'Notificaciones'} 
        subtitle={t('notifications_subtitle') || 'Gestiona las notificaciones del sitio'} 
      />
      
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <NotificationsTable
        loading={loading}
        notifications={notifications}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        typeFilter={typeFilter}
        onTypeFilterChange={handleTypeFilterChange}
        isReadFilter={isReadFilter}
        onIsReadFilterChange={handleIsReadFilterChange}
        totalRows={totalRows}
        onView={handleView}
        onMarkAllAsRead={handleMarkAllAsRead}
        onRefresh={() =>
          fetchNotifications(
            paginationModel.page + 1,
            paginationModel.pageSize,
            debouncedSearchTerm,
            typeFilter,
            isReadFilter
          )
        }
      />

      {/* Modal Ver Detalles */}
      <Dialog 
        open={isViewModalOpen} 
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedNotification(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('notification_details') || 'Detalles de la Notificación'}</Typography>
            {selectedNotification?.type && (
              <Chip 
                label={getTypeLabel(selectedNotification.type)} 
                size="small" 
                color={getTypeColor(selectedNotification.type)}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedNotification && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('title') || 'Título'}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {selectedNotification.title}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  {t('description') || 'Descripción'}
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedNotification.description}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setIsViewModalOpen(false);
            setSelectedNotification(null);
          }}>
            {t('close') || 'Cerrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de detalles de orden (para notificaciones de tipo order) */}
      <OrderDetailModal
        open={isOrderDetailModalOpen}
        onClose={() => {
          setIsOrderDetailModalOpen(false);
          setOrderDetail(null);
        }}
        order={orderDetail}
      />
    </Box>
  );
};

export default Notifications;
